import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { OidcService } from './oidc.service';
import { AppConfigService } from './app-config.service';
import { take } from 'rxjs';
import { Constants } from '../../app.constants';

/**
 * Monitors page visibility and checks authentication when tab becomes active
 * Handles cases where silent renewal failed while tab was inactive
 */
@Injectable({
  providedIn: 'root'
})
export class PageVisibilityService implements OnDestroy {
  private visibilityChangeHandler: (() => void) | null = null;
  private lastCheckTime: number = Date.now();
  private readonly CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

  constructor(
    private oidcService: OidcService,
    private appConfigService: AppConfigService,
    private router: Router
  ) {}

  /**
   * Start monitoring page visibility
   * Call this in app.component.ts ngOnInit()
   */
  startMonitoring(): void {
    if (this.visibilityChangeHandler) {
      return; // Already monitoring
    }

    this.visibilityChangeHandler = () => {
      if (!document.hidden) {
        this.handlePageBecameVisible();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    console.log('[PageVisibility] Started monitoring page visibility');
  }

  /**
   * Stop monitoring
   */
  stopMonitoring(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
      console.log('[PageVisibility] Stopped monitoring');
    }
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }

  /**
   * Handle page becoming visible after being hidden
   */
  private handlePageBecameVisible(): void {
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastCheckTime;

    console.log('[PageVisibility] Page became visible', {
      timeSinceLastCheck: Math.floor(timeSinceLastCheck / 1000) + 's'
    });

    // Only check if page was hidden for more than 5 minutes
    if (timeSinceLastCheck < this.CHECK_INTERVAL_MS) {
      console.log('[PageVisibility] Recently checked, skipping');
      this.lastCheckTime = now;
      return;
    }

    this.lastCheckTime = now;
    this.checkAuthenticationStatus();
  }

  /**
   * Check if user is still authenticated
   */
  private checkAuthenticationStatus(): void {
    console.log('[PageVisibility] Checking authentication status...');

    this.oidcService.isAuthenticated$
      .pipe(take(1))
      .subscribe((isAuthenticated) => {
        if (!isAuthenticated) {
          console.warn('[PageVisibility] User not authenticated - checking with IDP');
          this.performAuthCheck();
        } else {
          console.log('[PageVisibility] User authenticated - checking token validity');
          this.checkTokenValidity();
        }
      });
  }

  /**
   * Perform full auth check with IDP
   */
  private performAuthCheck(): void {
    this.oidcService.checkAuth()
      .pipe(take(1))
      .subscribe({
        next: (loginResponse) => {
          if (!loginResponse.isAuthenticated) {
            console.warn('[PageVisibility] Auth check failed - redirecting to login');
            this.redirectToLogin();
          } else {
            console.log('[PageVisibility] Auth check succeeded');
          }
        },
        error: (error) => {
          console.error('[PageVisibility] Auth check error:', error);
          this.redirectToLogin();
        }
      });
  }

  /**
   * Check if current token is still valid
   */
  private checkTokenValidity(): void {
    this.oidcService.getAccessToken()
      .pipe(take(1))
      .subscribe((token) => {
        if (!token) {
          console.warn('[PageVisibility] No access token found');
          this.performAuthCheck();
          return;
        }

        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const now = Math.floor(Date.now() / 1000);
          const expiresAt = payload.exp;

          if (now >= expiresAt) {
            console.warn('[PageVisibility] Token expired - attempting renewal');
            this.performAuthCheck();
          } else {
            const timeLeft = expiresAt - now;
            console.log('[PageVisibility] Token valid for', Math.floor(timeLeft / 60), 'minutes');
          }
        } catch (error) {
          console.error('[PageVisibility] Error decoding token:', error);
          this.performAuthCheck();
        }
      });
  }

  /**
   * Redirect to login using the OIDC authorize flow
   */
  private redirectToLogin(): void {
    console.warn('[PageVisibility] Token expired - redirecting to login');

    // Save current URL for post-login redirect
    const currentUrl = this.router.url;
    if (currentUrl && currentUrl !== '/' && !currentUrl.includes('/callback')) {
      sessionStorage.setItem(Constants.WEB_STORAGE_KEY.REDIRECT, currentUrl);
    }

    // Clear auth storage before redirecting
    this.oidcService.clearAuthStorage();

    // Use OIDC authorize flow (handles redirect URLs from runtime config)
    this.oidcService.authorize();
  }
}
